import clsx from 'clsx';
import * as styles from '../../styles/components/Header.less';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import Select, { StylesConfig } from 'react-select';
import Authenticated from '../../containers/Authenticated';
import Superuser from '../../containers/Superuser';
//
interface LangOption {
  label: string;
  value: string;
  index: number;
}

interface HeaderOwnProps {
  isSuperuserActive?: boolean;
  onChangeSuperuserActive?: React.MouseEventHandler<HTMLButtonElement>;
  onLogout?: React.MouseEventHandler<HTMLElement>;
}

const options: LangOption[] = [
  { label: 'Suomi', value: 'fi', index: 0 },
  { label: 'Svenska', value: 'sv', index: 1 },
  { label: 'English', value: 'en', index: 2 },
];
//--------------------------------------------------------------------
// Returns valid lang option index in all cases, thus use this if
// value may have illegal value.
//--------------------------------------------------------------------
const getBrowserLangSelection = (value: string) => {
  for (let i = 0, ilen = options.length; i < ilen; i++) {
    if (options[i].value === value) {
      return i;
    };
  };
  return 0;
};
//
const colorStyles: StylesConfig<LangOption, false> = {
  option: (provided, { isFocused }) => ({ // option list
    ...provided,
    color: isFocused ? 'white' : 'black',
    fontWeight: isFocused ? 600 : 400,
    backgroundColor: isFocused ? 'rgba(76, 169, 47, 1)' : '',
  }),
  control: provided => ({
    ...provided,
    color: 'black',
  }),
  singleValue: provided => ({ // selected value
    ...provided,
    color: 'rgba(76, 169, 47, 1)',
    fontWeight: 800,
    width: '70px',
  })
};
//
const Header = (props: HeaderOwnProps) => {
  // Subscribe to i18n from inside the component, not via a withTranslation HOC
  // wrapped around React.memo: the HOC's re-render (when i18next becomes ready,
  // or on changeLanguage) reaches the memo boundary as props and gets shallow-
  // compared away, so the first paint's untranslated keys stick until some other
  // state change forces a render. The hook re-renders Header itself instead.
  const { t, i18n } = useTranslation('common');

  // A saved language index means the user has changed the language before;
  // in that case we start with the language menu closed.
  const savedIndex = localStorage.getItem('setLangOptionIndex');
  const hasSaved = savedIndex !== undefined && savedIndex !== 'undefined' && savedIndex !== null;

  const [selectedOption, setSelectedOption] = useState<LangOption>(
    () => (hasSaved ? options[Number(savedIndex)] : options[getBrowserLangSelection(i18n.language)])
  );
  const [menuActive, setMenuActive] = useState(hasSaved);

  // Apply the saved language on mount (was a side effect in the constructor).
  useEffect(() => {
    if (hasSaved) {
      i18n.changeLanguage(options[Number(savedIndex)].value);
    }
    // run once on mount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectedOption = (option: LangOption | null) => {
    if (!option) {
      console.warn('+++ selectedOptions is undefined');
    } else {
      localStorage.setItem('setLangOptionIndex', String(option.index));
      setSelectedOption(option);
      i18n.changeLanguage(option.value);
    }
  };

  const onDotsClick = () => setMenuActive((prev) => !prev);

  const title = t('main.admin');

  return (
    <header className={styles.header}>
      <h1 className={styles.title}><Link className="fa fa-home" to="/"> {title}</Link></h1>
      <div className={styles.actions}>
        <Authenticated>
          <div className={styles['actions-container']}>
            <Superuser>
              <div>
                <button className={clsx(styles['super-button'], props.isSuperuserActive ? 'button alert' : 'button success')} type="button"

                  title="Superuser/Admin"
                  onClick={props.onChangeSuperuserActive}>
                  <i className={props.isSuperuserActive ? "fa fa-toggle-on" : "fa fa-toggle-off"} aria-hidden="true"></i>
                  {props.isSuperuserActive ? 'Super' : 'Admin'}
                </button>
              </div>
            </Superuser>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ float: 'right' }} className={styles['logout-action']}>
                {/* No onClick on the icon — it bubbles to the anchor, and the
                    duplicate handler fired logout (and its redirect) twice. */}
                <a className={styles.logout} onClick={props.onLogout} href="/">
                  {t('login.out')} <i className="fa fa-sign-out" aria-hidden="true"></i>
                </a>
              </div>
              <div className={styles['menu-container']}>
                <div id="lang-menu" className={menuActive ? styles['lang-menu-hidden'] : styles['lang-menu']}>
                  <Select
                    value={selectedOption}
                    onChange={handleSelectedOption}
                    options={options}
                    styles={colorStyles}
                    defaultMenuIsOpen={menuActive}
                    openMenuOnFocus={true}
                  />
                </div>
                <div className={styles['lang-menu-button']}>
                  <button className={styles['lang-menu-dots']} onClick={onDotsClick}>
                    <i className="fa fa-ellipsis-v"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Authenticated>
      </div>
    </header>
  );
};

export default React.memo(Header);
